import { forwardRef, useState, InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes } from 'react';
import FaIcon from './FaIcon';
import Select from './Select';

// =============================================================================
// INPUT COMPONENT
// =============================================================================
// Standardized input component with consistent styling, error states, and
// accessibility features

export type InputVariant = 'default' | 'error' | 'success';

interface BaseInputProps {
  variant?: InputVariant;
  fullWidth?: boolean;
  /**
   * Show the reveal toggle on `type="password"` (default true).
   *
   * Set false only where the call site draws its own -- SetupAccount and the
   * interface-credential modal already did, and would otherwise get two.
   */
  revealable?: boolean;
}

// Text Input
interface TextInputProps extends BaseInputProps, InputHTMLAttributes<HTMLInputElement> {
  as?: 'input';
}

// Textarea
interface TextareaInputProps extends BaseInputProps, TextareaHTMLAttributes<HTMLTextAreaElement> {
  as: 'textarea';
  rows?: number;
}

// Select
interface SelectInputProps extends BaseInputProps, SelectHTMLAttributes<HTMLSelectElement> {
  as: 'select';
}

type InputProps = TextInputProps | TextareaInputProps | SelectInputProps;

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, InputProps>(
  ({ variant = 'default', fullWidth = true, className = '', as = 'input', revealable = true, ...props }, ref) => {
    const [revealed, setRevealed] = useState(false);
    // Variant styles — focus glow uses semi-transparent ring for depth
    const variantStyles: Record<InputVariant, string> = {
      default: [
        'border-theme-secondary',
        'hover:border-gray-400 dark:hover:border-gray-500',
        'focus:border-primary-500 focus:ring-primary-500/30',
        'dark:focus:border-primary-400 dark:focus:ring-primary-400/20',
      ].join(' '),
      error: [
        'border-red-400 dark:border-red-500',
        'hover:border-red-500 dark:hover:border-red-400',
        'focus:border-red-500 focus:ring-red-500/30',
        'dark:focus:border-red-400 dark:focus:ring-red-400/20',
        'pr-10',
      ].join(' '),
      success: [
        'border-green-400 dark:border-green-500',
        'hover:border-green-500 dark:hover:border-green-400',
        'focus:border-green-500 focus:ring-green-500/30',
        'dark:focus:border-green-400 dark:focus:ring-green-400/20',
        'pr-10',
      ].join(' '),
    };

    const baseStyles = `
      px-3 py-2
      border rounded-lg
      bg-white dark:bg-gray-800/50
      text-theme-primary
      placeholder-gray-400 dark:placeholder-gray-500
      text-sm
      transition-all duration-200
      focus:outline-none focus:ring-2
      disabled:opacity-60 disabled:cursor-not-allowed
      disabled:hover:border-gray-300 dark:disabled:hover:border-gray-600
      disabled:bg-gray-100 dark:disabled:bg-gray-800/30
    `.replace(/\s+/g, ' ').trim();

    const widthStyle = fullWidth ? 'w-full' : '';
    const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${widthStyle} ${className}`;

    if (as === 'textarea') {
      const { rows = 3, ...textareaProps } = props as TextareaInputProps;
      return (
        <textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          rows={rows}
          className={combinedClassName}
          {...textareaProps}
        />
      );
    }

    if (as === 'select') {
      // Delegates to Select, which draws its own list. A native <select> can be
      // styled shut but not open -- the popup belongs to the operating system.
      // Every `<Input as="select">` in the app changes with this one line.
      const selectProps = props as SelectInputProps;
      return <Select variant={variant} fullWidth={fullWidth} className={className} {...selectProps} />;
    }

    const inputProps = props as TextInputProps;

    /*
     * A password field people can read back.
     *
     * Typing a password blind is how lockouts happen: `/api/auth/login` has a
     * per-account lockout with escalating cooldowns, so a few mistypes on a
     * phone keypad -- in the rain, in a vehicle, in gloves -- keep a responder
     * out of the platform during the event they were called to. On the
     * set-a-password screens it is worse: the rules are strict enough that
     * "it will not accept my password" and "I am typing it wrong" look
     * identical.
     *
     * Built in here rather than added to each screen because that sweep never
     * finishes: three call sites had rolled their own toggle and sixteen fields
     * across nine files had none. Every password `<Input>` in the app, and every
     * future one, gets it from this branch.
     *
     * The button is 44px so it is a real touch target, and the input reserves
     * exactly that much padding so a tap on the last characters lands in the
     * field, not on the toggle.
     */
    if (inputProps.type === 'password' && revealable) {
      const { type: _ignored, ...rest } = inputProps;
      return (
        <div className={`relative ${fullWidth ? 'w-full' : 'inline-block'}`}>
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            type={revealed ? 'text' : 'password'}
            className={`${combinedClassName} pr-11`}
            {...rest}
          />
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            disabled={inputProps.disabled}
            /*
             * Deliberately IN the tab order. Taking it out costs a
             * keyboard-only user the control entirely -- a screen reader can
             * still reach it with a virtual cursor, but someone driving the
             * page from the keyboard with sight cannot. One extra Tab between
             * the field and the submit button is the cheaper trade.
             */
            aria-label={revealed ? 'Hide password' : 'Show password'}
            title={revealed ? 'Hide password' : 'Show password'}
            className="absolute right-0 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-lg text-theme-tertiary hover:text-theme-primary disabled:cursor-not-allowed disabled:opacity-60 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--interactive-focus-ring)]"
          >
            <FaIcon icon={revealed ? 'eye-slash' : 'eye'} className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return (
      <input
        ref={ref as React.Ref<HTMLInputElement>}
        className={combinedClassName}
        {...inputProps}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input;

// =============================================================================
// VALIDATION ICONS
// =============================================================================
// Icons to show validation state without relying on color alone

export function ErrorIcon({ className = '' }: { className?: string }) {
  return (
    <FaIcon icon="circle-exclamation" className={`h-5 w-5 text-red-500 ${className}`} />
  );
}

export function SuccessIcon({ className = '' }: { className?: string }) {
  return (
    <FaIcon icon="circle-check" className={`h-5 w-5 text-green-600 dark:text-green-400 ${className}`} />
  );
}
